/**
 * English translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const en: TranslationKeys = {
  controls: {
    maxTime: 'Max Time',
    maxTimeMinutes: 'Max Time (minutes)',
    travelMode: 'Travel Mode',
    speed: 'Speed',
    mapStyle: 'Map Style',
    language: 'Language',
    share: 'Share',
    play: 'Play',
    pause: 'Pause',
    restart: 'Restart',
    loading: 'Loading...',
    showControls: 'Show Controls',
    hideControls: 'Hide Controls',
    expandControls: 'Expand controls',
    collapseControls: 'Collapse controls',
    selectLocationHint: 'Please select a location on the map to start',
  },
  travelModes: {
    driving: 'Driving',
    walking: 'Walking',
    cycling: 'Cycling',
    transit: 'Transit',
  },
  speed: {
    verySlow: '0.1x (Very Slow)',
    slow: '0.25x (Slow)',
    half: '0.5x (Half)',
    normal: '1x (Normal)',
    fast: '1.5x (Fast)',
    veryFast: '2x (Very Fast)',
  },
  mapStyles: {
    streets: 'Streets',
    satellite: 'Satellite',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: 'Search Address',
    placeholder: 'Enter an address to calculate time map',
    noResults: 'No results found',
    loading: 'Loading suggestions',
  },
  error: {
    retry: 'Retry',
    dismiss: 'Dismiss error',
    somethingWentWrong: 'Something went wrong. Please refresh the page.',
    browserNotSupported: 'Your browser does not support this application. Please use a modern browser.',
    usageLimitReached: 'You have reached the beta limit of 5 calculations. Thank you for trying out! DM me if you are interested in this project.',
  },
  progress: {
    minutes: 'min',
  },
  share: {
    urlCopied: 'URL copied to clipboard!',
    shareUrl: 'Share URL',
  },
  emailGate: {
    title: 'Sign in to Timed Map Beta',
    subtitle: 'Enter your email address to receive a login link',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: 'Email address',
    sendButton: 'Send Login Link',
    sendingButton: 'Sending login link...',
    checkEmailTitle: 'Check your email',
    checkEmailMessage: 'We\'ve sent a login link to {email}',
    checkEmailInstruction: 'Click the link in the email to continue to the timed map.',
    checkEmailHelp: 'Didn\'t receive the email? Check your spam folder or try again with a different email address.',
    emailRequired: 'Please enter your email address',
    emailInvalid: 'Please enter a valid email address',
    domainRestriction: 'We only allow @gmail.com, @outlook.com, and @qq.com email addresses for beta access.',
    domainNotAllowed: 'Beta access is currently limited to Gmail, Outlook, and QQ email addresses',
    typoSuggestion: 'Did you mean @{domain}?',
    rateLimitExceeded: 'Too many requests. Please wait a moment and try again.',
    invalidEmailFormat: 'Invalid email address. Please check and try again.',
    failedToSend: 'Failed to send login link. Please try again.',
    magicLinkFailed: 'Failed to send magic link: {error}',
    testMeButton: 'Test Me',
    bannerText: 'Timed Map',
    betaTag: 'Beta',
  },
  testCode: {
    title: 'Enter Test Code',
    subtitle: 'Have a test code? Enter it below to get started',
    placeholder: 'Enter code',
    button: 'Continue with Code',
    haveCodeLink: 'Have a test code?',
    useEmailLink: 'Use email instead',
    invalid: 'Invalid test code',
    expired: 'This test code has expired',
    limitReached: 'This test code has reached its usage limit',
    optionalEmailTitle: 'Save Your Progress (Optional)',
    optionalEmailSubtitle: 'Want to save your work? Enter your email below (or skip for now)',
    optionalEmailPlaceholder: 'your.email@example.com (optional)',
    skipButton: 'Skip for now',
    submitEmailButton: 'Continue',
  },
  usageIndicator: {
    template: '{count} calculations remaining',
    loading: 'Loading...',
  },
  disclaimer: {
    loading: 'Loading...',
    title: 'Important Disclaimer',
    content: 'This is a beta and experimental project. All map data is provided by Mapbox, a third-party service, and has not been independently verified. Maps may contain inaccuracies and should not be relied upon for critical decisions. Travel time calculations are estimates only and have not been verified for accuracy. We assume no responsibility or liability for any inaccuracies, errors, or consequences arising from the use of this application.',
    consent: 'By continuing to use this application, you acknowledge and accept all of the above terms and disclaimers.',
  },
  intro: {
    title: 'Discover Your Reach',
    message: 'Visualise how far you can travel in a given time from any location.\n\nSee your travel-time reachability animated on the map to make better decisions about where to live, work, or visit.',
    step1: 'Search or click on the map to set your starting point',
    step2: 'The map shows your travel-time reachability with animated zones',
    step3: 'Configure your travel time and transportation mode',
    step4: 'Click Play to watch your reachability expand over time',
    step5: 'Share your visualization with others via a shareable link',
    next: 'Next',
    skip: 'Skip Tour',
    gotIt: 'Got it!',
    startTour: 'Start Tour',
  },
};

